import { renderedRows } from './renderModel';
import { formatInstant } from '@/lib/utils/date';
import { activeEvents, displayPayload } from './projection';
import type { ClinicalEvent, ClinicalSubject } from './types';
/** Spreadsheet quoting does not neutralize formulas; prefix dangerous text before quoting. */
export function csvCell(value: string) {
  const safe = /^[\s]*[=+@-]/.test(value) ? `'${value}` : value;
  return `"${safe.replaceAll('"', '""')}"`;
}
export function observationRows(events: ClinicalEvent[], subjects: ClinicalSubject[], timeZone: string): string[][] {
  return [
    [
      'Observed time',
      'Subject',
      'Observation',
      'Finding (canonical units)',
      'Author initials',
      'Role',
      'Source',
      'Event ID',
    ],
    ...activeEvents(events).map((event) => [
      formatInstant(event.occurred_at, 'yyyy-MM-dd HH:mm:ss XXX', timeZone),
      subjects.find((s) => s.id === event.subject_id)?.label ?? 'Unknown subject',
      event.event_type,
      displayPayload(event.payload),
      event.author_initials,
      event.author_role,
      event.source,
      event.id,
    ]),
  ];
}
export function prenatalCsv(events: ClinicalEvent[], subjects: ClinicalSubject[], timeZone: string) {
  return (
    '\uFEFF' +
    observationRows(events, subjects, timeZone)
      .map((row) => row.map(csvCell).join(','))
      .join('\r\n')
  );
}
export function downloadPrenatalCsv(events: ClinicalEvent[], subjects: ClinicalSubject[], timeZone: string) {
  const url = URL.createObjectURL(
    new Blob([prenatalCsv(events, subjects, timeZone)], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = 'prenatal-confirmed-observations.csv';
  link.click();
  URL.revokeObjectURL(url);
}
const escapeHtml = (text: string) =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
export function prenatalPrintHtml(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  context: string,
) {
  const [head, ...rows] = observationRows(events, subjects, timeZone);
  return `<!doctype html><html><head><meta charset="utf-8"><title>Prenatal observations</title><style>@page{size:landscape;margin:12mm}body{font:11px sans-serif;color:#111}table{border-collapse:collapse;width:100%}th,td{border:1px solid #bbb;padding:5px;text-align:left;vertical-align:top}thead{display:table-header-group}tr{break-inside:avoid}</style></head><body><h1>Prenatal confirmed observations</h1><p>${escapeHtml(context)}</p><p>Timezone: ${escapeHtml(timeZone)}. Includes current confirmed observations only; pending and struck records are excluded. This is not the complete audit record.</p><table><thead><tr>${head.map((v) => `<th>${escapeHtml(v)}</th>`).join('')}</tr></thead><tbody>${rows.map((row) => `<tr>${row.map((v) => `<td>${escapeHtml(v)}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`;
}
export function printPrenatal(events: ClinicalEvent[], subjects: ClinicalSubject[], timeZone: string, context: string) {
  const frame = document.createElement('iframe');
  frame.title = 'Print prenatal observations';
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.onload = () => {
    const target = frame.contentWindow;
    if (!target) {
      frame.remove();
      return;
    }
    target.addEventListener('afterprint', () => frame.remove(), { once: true });
    target.print();
  };
  frame.srcdoc = prenatalPrintHtml(events, subjects, timeZone, context);
  document.body.append(frame);
}

export interface RenderedExportOptions {
  edd: string | null;
  definition?: import('./definitions').FlowsheetDefinition;
  numbersOnly?: boolean;
  prePregnancyWeightGrams?: number | null;
}
export function renderedPrenatalCsv(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  options: RenderedExportOptions,
) {
  return (
    '\uFEFF' +
    renderedRows(
      events,
      subjects,
      timeZone,
      options.edd,
      options.definition,
      options.numbersOnly,
      options.prePregnancyWeightGrams,
    )
      .map((row) => row.map(csvCell).join(','))
      .join('\r\n')
  );
}
export function renderedPrenatalPrintHtml(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  context: string,
  options: RenderedExportOptions,
) {
  const [headers, ...rows] = renderedRows(
    events,
    subjects,
    timeZone,
    options.edd,
    options.definition,
    options.numbersOnly,
    options.prePregnancyWeightGrams,
  );
  const panels: number[][] = [];
  const observationIndices = headers.slice(2, -2).map((_, i) => i + 2);
  for (let i = 0; i < Math.max(1, observationIndices.length); i += 4)
    panels.push([0, 1, ...observationIndices.slice(i, i + 4), headers.length - 2, headers.length - 1]);
  return `<!doctype html><html><head><meta charset="utf-8"><title>Prenatal flowsheet</title><style>@page{size:landscape;margin:12mm;@bottom-right{content:"Page " counter(page) " / " counter(pages)}}body{font:11px sans-serif;color:#111}section+section{break-before:page}table{border-collapse:collapse;width:100%;table-layout:fixed}th,td{border:1px solid #bbb;padding:5px;vertical-align:top;overflow-wrap:anywhere;white-space:pre-wrap}thead{display:table-header-group}tr{break-inside:avoid}</style></head><body>${panels.map((indices, i) => `<section><h1>Prenatal flowsheet · column panel ${i + 1} of ${panels.length}</h1><p>${escapeHtml(context)}</p><p>Timezone: ${escapeHtml(timeZone)}. Current confirmed observations. Pending and struck records excluded; this is not the legal audit record.</p><table><thead><tr><th colspan="${indices.length}">${escapeHtml(context)} · column panel ${i + 1}</th></tr><tr>${indices.map((j) => `<th>${escapeHtml(headers[j])}</th>`).join('')}</tr></thead><tbody>${rows.map((row) => `<tr>${indices.map((j) => `<td>${escapeHtml(row[j])}</td>`).join('')}</tr>`).join('')}</tbody></table></section>`).join('')}</body></html>`;
}
export function downloadRenderedPrenatalCsv(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  options: RenderedExportOptions,
) {
  const url = URL.createObjectURL(
    new Blob([renderedPrenatalCsv(events, subjects, timeZone, options)], { type: 'text/csv;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = 'prenatal-flowsheet.csv';
  a.click();
  URL.revokeObjectURL(url);
}
export function printRenderedPrenatal(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  context: string,
  options: RenderedExportOptions,
) {
  const frame = document.createElement('iframe');
  frame.title = 'Print prenatal flowsheet';
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0';
  frame.onload = () => {
    const target = frame.contentWindow;
    if (!target) {
      frame.remove();
      return;
    }
    target.addEventListener('afterprint', () => frame.remove(), { once: true });
    target.print();
  };
  frame.srcdoc = renderedPrenatalPrintHtml(events, subjects, timeZone, context, options);
  document.body.append(frame);
}
